import React, { useEffect, useRef, useState } from 'react'
import { shrinkImages } from 'src/helpers/commons/shrinkImage'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCardTitle,
  CCol,
  CForm,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CInputGroup,
  CInputGroupText,
  CRow,
} from '@coreui/react'
import { wtApiCall } from '../../helpers/commons/ApiWrapper'
import {
  lzNotifyErrorMessage,
  lzNotifyErrorMessageFromResult,
  lzNotifySuccessMessage,
} from '../../helpers/commons/notifications'
import Loading from '../../helpers/commons/loader'
import '../../../node_modules/font-awesome/css/font-awesome.min.css'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { APP_BASE_URL } from '../../config/Request'
import {
  PropertyTypeOptions,
  ListingStatusNew,
  ListingStatusAll,
  CurrencyOptions,
  FurnishingOptions,
  PlotUnitOptions,
  DefaultBills,
  DefaultFeatures,
} from 'src/helpers/commons/dropdownOptions'

const GREEN = '#1F4D3A'
const GOLD = '#CF9A41'

const emptyRecord = {
  property_type: '',
  status: 'For Sale',
  country: '',
  city_county: '',
  neighborhood_one: '',
  county: '',
  area_requested: false,
  full_address: '',
  currency: 'KES',
  original_price: '',
  furnishing: '',
  bed_rooms: '',
  rest_rooms: '',
  parking_space: '',
  sq_meter: '',
  year_established: '',
  features: [],
  plot_size: '',
  plot_unit: 'Acres',
  title_deed_ready: '',
  bills: [],
  disclosure: '',
  nearby: '',
  short_description: '',
  long_description: '',
}

// ---------- small building blocks (kept outside the form so typing never loses focus) ----------

const getMyRole = () => {
  try {
    let u = wtGetFromLocalStorage('authUser') || {}
    if (typeof u === 'string') u = JSON.parse(u)
    return u.role_name || (u.user && u.user.role_name) || ''
  } catch (e) {
    return ''
  }
  
}

const pillStyle = (on, disabled) => ({
  border: `1px solid ${on ? GREEN : '#c9ced6'}`,
  background: on ? GREEN : '#fff',
  color: on ? '#fff' : '#333',
  borderRadius: 20,
  padding: '5px 14px',
  fontSize: 14,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled && !on ? 0.6 : 1,
  userSelect: 'none',
})

const Section = ({ title, hint, children }) => (
  <div style={{ border: '1px solid #e3e6ea', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <h6
      style={{
        color: GREEN,
        fontWeight: 600,
        borderLeft: `4px solid ${GOLD}`,
        paddingLeft: 8,
        marginBottom: hint ? 4 : 14,
      }}
    >
      {title}
    </h6>
    {hint && <div className="text-medium-emphasis small mb-3">{hint}</div>}
    <CRow>{children}</CRow>
  </div>
)

const Field = ({ label, required, md = 6, note, children }) => (
  <CCol md={md} className="mb-3">
    <label className="form-label">
      {label}
      {required && <span style={{ color: '#d93737' }}> *</span>}
    </label>
    {children}
    {note && <div className="text-medium-emphasis small mt-1">{note}</div>}
  </CCol>
)

const Pills = ({ options, value, onChange, disabled }) => (
  <div className="d-flex flex-wrap gap-2">
    {options.map((opt) => (
      <button
        type="button"
        key={opt}
        disabled={disabled}
        onClick={() => onChange(opt)}
        style={pillStyle(value === opt, disabled)}
      >
        {opt}
      </button>
    ))}
  </div>
)

// Tick tabs + a "+" box to add your own (Enter or + adds it, × removes a custom one)
function TagPicker({ defaults, value, onChange, disabled, placeholder }) {
  const [text, setText] = useState('')
  const selected = Array.isArray(value) ? value : []
  const custom = selected.filter((v) => !defaults.includes(v))
  const all = [...defaults, ...custom]

  const toggle = (item) => {
    if (disabled) return
    onChange(selected.includes(item) ? selected.filter((v) => v !== item) : [...selected, item])
  }
  const remove = (item) => onChange(selected.filter((v) => v !== item))
  const add = () => {
    const t = text.trim()
    if (!t) return
    const existing = all.find((v) => v.toLowerCase() === t.toLowerCase())
    if (existing) {
      if (!selected.includes(existing)) onChange([...selected, existing])
    } else {
      onChange([...selected, t])
    }
    setText('')
  }

  return (
    <div className="d-flex flex-wrap gap-2 align-items-center">
      {all.map((item) => {
        const on = selected.includes(item)
        const isCustom = !defaults.includes(item)
        return (
          <span
            key={item}
            onClick={() => toggle(item)}
            style={{ ...pillStyle(on, disabled), display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {on && <i className="fa fa-check" />} {item}
            {isCustom && !disabled && (
              <i
                className="fa fa-times"
                title="Remove"
                onClick={(e) => {
                  e.stopPropagation()
                  remove(item)
                }}
              />
            )}
          </span>
        )
      })}
      {!disabled && (
        <span
          style={{
            display: 'inline-flex',
            border: `1px dashed ${GOLD}`,
            borderRadius: 20,
            overflow: 'hidden',
          }}
        >
          <input
            value={text}
            maxLength={50}
            placeholder={placeholder}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                add()
              }
            }}
            style={{ border: 'none', outline: 'none', padding: '5px 10px', fontSize: 14, width: 150 }}
          />
          <button
            type="button"
            onClick={add}
            style={{ border: 'none', background: GOLD, color: '#fff', padding: '0 12px' }}
          >
            +
          </button>
        </span>
      )}
    </div>
  )
}

// Photo picker: thumbnails + a small "+ Add photo" tile (click or drop photos on it)
function PhotoPicker({ files, onChange, multiple, size = 150, mainLabel, disabled, addLabel }) {
  const inputRef = useRef(null)
  const [urls, setUrls] = useState([])
  const list = Array.isArray(files) ? files : []

  useEffect(() => {
    const made = list.map((f) => URL.createObjectURL(f))
    setUrls(made)
    return () => made.forEach((u) => URL.revokeObjectURL(u))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  const addFiles = (picked) => {
    const imgs = Array.from(picked || []).filter((f) => f.type && f.type.startsWith('image/'))
    if (!imgs.length) return
    onChange(multiple ? [...list, ...imgs] : [imgs[0]])
  }
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i))

  const tileBase = {
    position: 'relative',
    width: size,
    height: size,
    borderRadius: 8,
    overflow: 'hidden',
    flex: '0 0 auto',
  }
  const badge = {
    position: 'absolute',
    fontSize: 11,
    color: '#fff',
    padding: '1px 8px',
    borderRadius: 10,
  }

  return (
    <div className="d-flex flex-wrap gap-3">
      {list.map((f, i) => (
        <div
          key={urls[i] || i}
          title={f.name}
          style={{ ...tileBase, border: `2px solid ${mainLabel ? GOLD : '#e3e6ea'}`, background: '#f5f6f8' }}
        >
          {urls[i] && (
            <img
              src={urls[i]}
              alt={f.name}
              onClick={() => window.open(urls[i], '_blank')}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
            />
          )}
          {mainLabel && (
            <span style={{ ...badge, top: 6, left: 6, background: GOLD, fontWeight: 600 }}>Main</span>
          )}
          {multiple && (
            <span style={{ ...badge, bottom: 6, right: 6, background: 'rgba(0,0,0,0.55)' }}>{i + 1}</span>
          )}
          {!disabled && (
            <button
              type="button"
              title="Remove"
              onClick={() => remove(i)}
              style={{
                position: 'absolute',
                top: 6,
                right: 6,
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              <i className="fa fa-times" />
            </button>
          )}
        </div>
      ))}

      {!disabled && (multiple || list.length === 0) && (
        <div
          onClick={() => inputRef.current && inputRef.current.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            addFiles(e.dataTransfer.files)
          }}
          style={{
            ...tileBase,
            border: `2px dashed ${GOLD}`,
            background: '#fdf9f2',
            color: GREEN,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: 30, lineHeight: 1 }}>+</div>
          <div className="small mt-1">{addLabel || (multiple ? 'Add photos' : 'Add photo')}</div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={!!multiple}
        style={{ display: 'none' }}
        onChange={(e) => {
          addFiles(e.target.files)
          e.target.value = ''
        }}
      />
    </div>
  )
}

// A photo that is already saved on the server (optional × to remove)
const ExistingTile = ({ src, size = 150, main, onRemove }) => (
  <div
    style={{
      position: 'relative',
      width: size,
      height: size,
      borderRadius: 8,
      overflow: 'hidden',
      flex: '0 0 auto',
      border: `2px solid ${main ? GOLD : '#e3e6ea'}`,
      background: '#f5f6f8',
    }}
  >
    <img
      src={src}
      alt=""
      onClick={() => window.open(src, '_blank')}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
    />
    {main && (
      <span
        style={{
          position: 'absolute',
          top: 6,
          left: 6,
          background: GOLD,
          color: '#fff',
          fontSize: 11,
          fontWeight: 600,
          padding: '1px 8px',
          borderRadius: 10,
        }}
      >
        Main
      </span>
    )}
    {onRemove && (
      <button
        type="button"
        title="Remove"
        onClick={onRemove}
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          width: 26,
          height: 26,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        <i className="fa fa-times" />
      </button>
    )}
  </div>
)

// Turn what the server sends into form values
const fromServer = (p) => {
  const r = { ...emptyRecord }
  Object.keys(emptyRecord).forEach((k) => {
    if (p[k] !== null && p[k] !== undefined) r[k] = p[k]
  })
  r.features = Array.isArray(p.features) ? p.features : []
  r.bills = Array.isArray(p.bills) ? p.bills : []
  r.title_deed_ready =
    p.title_deed_ready === null || p.title_deed_ready === undefined ? '' : p.title_deed_ready ? 1 : 0
  if (!r.currency) r.currency = 'KES'
  return r
}

// ---------- the form ----------
// Searchable Town / City picker (shows "Ruiru · Kiambu County")
export const AreaRequestBox = ({ propertyId, townId, townName, requested }) => {
  const [mode, setMode] = useState('add')
  const [name, setName] = useState(requested || '')
  const [list, setList] = useState([])
  const [matchId, setMatchId] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [done, setDone] = useState('')

  useEffect(() => {
    setName(requested || '')
  }, [requested])

  useEffect(() => {
    if (!townId) return
    wtApiCall('locations/towns/' + townId + '/areas', {}, 'get', (res) => setList(res.data || []), () => setList([]))
  }, [townId])

  const send = () => {
    if (mode === 'add' && !name.trim()) return setMsg('Please enter the area name.')
    if (mode === 'match' && !matchId) return setMsg('Please choose an area from the list.')
    setBusy(true)
    setMsg('')
    wtApiCall(
      'property/' + propertyId + '/area/' + mode,
      mode === 'add' ? { name: name.trim() } : { area_id: matchId },
      'post',
      (res) => {
        const saved = (res && res.data && res.data.area) || name.trim()
        setDone(saved)
        setTimeout(() => window.location.reload(), 1500)
      },
      (err) => {
        setBusy(false)
        setMsg((err && (err.message || (err.data && err.data.message))) || 'Could not save. Please try again.')
      },
    )
  }

  const pill = (active) => ({
    padding: '6px 14px',
    borderRadius: 20,
    border: '1px solid #CF9A41',
    background: active ? '#CF9A41' : '#fff',
    color: active ? '#fff' : '#1F4D3A',
    fontWeight: 600,
    cursor: 'pointer',
  })

  if (done) {
    return (
      <div style={{ background: '#e8f3ec', border: '1px solid #1F4D3A', borderRadius: 10, padding: 14, marginBottom: 16 }}>
        <strong style={{ color: '#1F4D3A' }}>Area saved: {done}.</strong> The listing can now be approved. Refreshing...
      </div>
    )
  }

  return (
    <div style={{ background: '#fdf6ea', border: '1px solid #CF9A41', borderRadius: 10, padding: 14, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, color: '#1F4D3A', marginBottom: 4 }}>New area requested</div>
      <div style={{ marginBottom: 10 }}>
        The agent requested an area that is not on the list: <strong>{requested || '-'}</strong>
        {townName ? ` (${townName})` : ''}. Please resolve it before approving.
      </div>
      {!townId ? (
        <div style={{ color: '#b42318' }}>The town on this listing is not in the town list. Please correct the town first.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <button type="button" style={pill(mode === 'add')} onClick={() => setMode('add')}>
              Add to list
            </button>
            <button type="button" style={pill(mode === 'match')} onClick={() => setMode('match')}>
              Match to existing
            </button>
          </div>
          {mode === 'add' ? (
            <input
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Area name (correct the spelling if needed)"
              spellCheck
            />
          ) : (
            <select className="form-select" value={matchId} onChange={(e) => setMatchId(e.target.value)}>
              <option value="">Choose the correct area...</option>
              {list.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
          {msg && <div style={{ color: '#b42318', marginTop: 8 }}>{msg}</div>}
          <button
            type="button"
            disabled={busy}
            onClick={send}
            style={{ marginTop: 10, padding: '8px 18px', borderRadius: 8, border: 0, background: '#1F4D3A', color: '#fff', fontWeight: 600 }}
          >
            {busy ? 'Saving...' : mode === 'add' ? 'Add area and save' : 'Save match'}
          </button>
        </>
      )}
    </div>
  )
}
const TownPicker = ({ towns, value, county, onPick, disabled }) => {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const shown = value ? `${value}${county ? ' · ' + county + ' County' : ''}` : ''
  const needle = q.trim().toLowerCase()
  const list = towns
    .filter((t) => !needle || t.name.toLowerCase().includes(needle) || t.county.toLowerCase().includes(needle))
    .slice(0, 60)

  return (
    <div style={{ position: 'relative' }}>
      <CFormInput
        value={open ? q : shown}
        placeholder="Start typing a town, e.g. Ruiru"
        onFocus={() => {
          setQ('')
          setOpen(true)
        }}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        disabled={disabled}
      />
      {open && !disabled && (
        <div
          style={{
            position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, maxHeight: 260, overflowY: 'auto',
            background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, boxShadow: '0 6px 16px rgba(0,0,0,.12)', marginTop: 4,
          }}
        >
          {list.length === 0 && (
            <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: '.9rem' }}>
              No matching town. Please contact KLS support to have it added.
            </div>
          )}
          {list.map((t) => (
            <div
              key={t.id}
              onMouseDown={(e) => {
                e.preventDefault()
                onPick(t)
                setOpen(false)
              }}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '.9rem' }}
            >
              <strong>{t.name}</strong> <span style={{ color: '#6b7280' }}>· {t.county} County</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const BedroomPills = ['Studio', '1', '2', '3', '4', '5', '6+']
const bedPill = (v) => {
  if (v === '' || v === null || v === undefined) return ''
  const n = Number(v)
  if (n === 0) return 'Studio'
  if (n >= 6) return '6+'
  return String(n)
}

export default function Form(props) {
  const isEdit = props.editId > 0
  const ro = !!props.viewOnly
  const locked = ro

  const [isLoading, setIsLoading] = useState(true)
  const [isDisabled, setIsDisabled] = useState(false)
  const [record, setRecord] = useState(emptyRecord)
  const [canSeeAddress, setCanSeeAddress] = useState(!isEdit)
  const [existingMain, setExistingMain] = useState('')
  const [existingImages, setExistingImages] = useState([])
  const [removeIds, setRemoveIds] = useState([])
  const photosLocked = ro
  const [FeaturedImage, setFeaturedImage] = useState([])
  const [Image, setImage] = useState([])
  const [rejectInfo, setRejectInfo] = useState('')
  const [towns, setTowns] = useState([])
  const [townId, setTownId] = useState(0)
  const [areas, setAreas] = useState([])
  const [areasLoaded, setAreasLoaded] = useState(false)
  const [areaMode, setAreaMode] = useState('list') // 'list' or 'other'

  const set = (key, value) => setRecord((p) => ({ ...p, [key]: value }))

  const type = record.property_type
  const isLand = type === 'Land'
  const hasRooms = type === 'House/Villa' || type === 'Apartment'
  const showAddress = canSeeAddress

  const getData = () => {
    if (!isEdit) {
      setIsLoading(false)
      return
    }
    wtApiCall(
      'property/' + props.editId,
      {},
      'get',
      (response) => {
        const { data } = response
        setRecord(fromServer(data.property || {}))
        setCanSeeAddress(!!data.can_see_address)
        if (data.property && Number(data.property.approval_status) === 2) {
        setRejectInfo(data.property.rejection_reason || 'No reason was given.') }
        setExistingMain((data.property && data.property.featured_image) || '')
        setExistingImages(data.property_images || [])
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

    // Load the town list once
  useEffect(() => {
    wtApiCall('locations/towns', {}, 'get', (res) => setTowns(res.data || []), () => setTowns([]))
  }, [])

  // Match the saved city to a town (on edit, once data and towns are loaded)
  useEffect(() => {
    if (!towns.length || isLoading) return
    const c = (record.city_county || '').trim().toLowerCase()
    if (!c) {
      setTownId(0)
      return
    }
    const hit =
      towns.find((t) => t.name.toLowerCase() === c && (!record.county || t.county === record.county)) ||
      towns.find((t) => t.name.toLowerCase() === c)
    setTownId(hit ? hit.id : 0)
    if (hit) setRecord((p) => ({ ...p, city_county: hit.name, county: hit.county }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [towns, isLoading])

  // Load the areas for the chosen town
  useEffect(() => {
    if (!townId) {
      setAreas([])
      setAreasLoaded(false)
      return
    }
    setAreasLoaded(false)
    wtApiCall(
      'locations/towns/' + townId + '/areas',
      {},
      'get',
      (res) => {
        setAreas(res.data || [])
        setAreasLoaded(true)
      },
      () => {
        setAreas([])
        setAreasLoaded(true)
      },
    )
  }, [townId])

  // Decide whether the saved area is on the list or a requested one
  useEffect(() => {
    if (!areasLoaded) return
    const a = (record.neighborhood_one || '').trim()
    if (!a) {
      setAreaMode('list')
      return
    }
    const hit = areas.find((x) => x.name.toLowerCase() === a.toLowerCase())
    if (hit) {
      setAreaMode('list')
      setRecord((p) => ({ ...p, neighborhood_one: hit.name, area_requested: false }))
    } else {
      setAreaMode('other')
      setRecord((p) => ({ ...p, area_requested: true }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areasLoaded, areas])

  const pickTown = (t) => {
    setRecord((p) => {
      const same = p.city_county === t.name && p.county === t.county
      return same
        ? p
        : { ...p, city_county: t.name, county: t.county, neighborhood_one: '', area_requested: false }
    })
    setTownId(t.id)
    setAreaMode('list')
  }

  const townNotListed = !!record.city_county && towns.length > 0 && !townId && !isLoading

  const save = () => {
    if (!locked) {
      if (!townId) {
        lzNotifyErrorMessage('Please select a town or city from the list.')
        return
      }
      if (areaMode === 'other' && !(record.neighborhood_one || '').trim()) {
        lzNotifyErrorMessage('Please type the area name, or select an area from the list.')
        return
      }
    }

    setIsDisabled(true)
    const payload = { ...record, country: 'Kenya' }
    if (!showAddress) delete payload.full_address

    // Shrink photos on the device first (faster upload, less data, stays under the server limit)
    Promise.all([
      shrinkImages(Array.isArray(FeaturedImage) ? FeaturedImage : []),
      shrinkImages(Array.isArray(Image) ? Image : []),
      ]).then(([mainPhoto, gallery]) => {
      const formdata = new FormData()
      formdata.append('record', JSON.stringify(payload))
      mainPhoto.forEach((f) => formdata.append('featured_image', f))
      gallery.forEach((f) => formdata.append('images[]', f))
      if (isEdit && removeIds.length) formdata.append('remove_images', JSON.stringify(removeIds))

      wtApiCall(
        'property' + (isEdit ? '/' + props.editId : ''),
        formdata,
        isEdit ? 'put' : 'post',
        (response) => {
          setIsDisabled(false)
          lzNotifySuccessMessage(response.data)
          props.onClose()
        },
        (error) => {
          setIsDisabled(false)
          lzNotifyErrorMessageFromResult(error)
        },
      )
    })
  }

  if (isLoading) {
    return Loading
  }

  const priceText =
    record.original_price !== '' && !isNaN(record.original_price)
      ? `${record.currency} ${Number(record.original_price).toLocaleString()}`
      : ''

  return (
    <CRow>
      <CCol md={12}>
        <CCard>
          <CCardHeader style={{ backgroundColor: GREEN }}>
            <CCardTitle className="text-white mb-0">
              {ro ? 'View' : isEdit ? 'Edit' : 'Add New'} Property
            </CCardTitle>
          </CCardHeader>
          <CCardBody>
            <CButton className="btn-sm mb-3" color="warning" onClick={() => props.onClose()}>
              <i className="fa fa-arrow-left"></i> Back
            </CButton>

            {isEdit && getMyRole() !== 'Agent' && record.area_requested && (
              <AreaRequestBox
                propertyId={record.id}
                townId={townId}
                townName={record.city_county}
                requested={record.neighborhood_one}
              />
            )}

            {rejectInfo && (
              <div
                className="small mb-3 p-2"
                style={{ background: '#fdecec', border: '1px solid #d93737', borderRadius: 6 }}
              >
               <strong>Not approved – changes required.</strong> Feedback from KLS: {rejectInfo}
                <br />
                Update the details and save to resubmit for approval.
              </div>
            )}



            <CForm onSubmit={(e) => e.preventDefault()}>
              {/* ===== BASICS ===== */}
              <Section title="Type">
                <Field label="Property type" required md={12}>
                  <Pills
                    options={PropertyTypeOptions}
                    value={type}
                    onChange={(v) => set('property_type', v)}
                    disabled={locked}
                  />
                </Field>
                <Field label="Listing" required md={12}>
                  <Pills
                    options={isEdit ? ListingStatusAll : ListingStatusNew}
                    value={record.status}
                    onChange={(v) => set('status', v)}
                    disabled={locked}
                  />
                </Field>
                
               <Field label="Town" required md={4}>
                  <TownPicker
                    towns={towns}
                    value={record.city_county}
                    county={townId ? record.county : ''}
                    onPick={pickTown}
                    disabled={locked}
                  />
                  {townNotListed && (
                    <div style={{ color: '#b42318', fontSize: '.8rem', marginTop: 4 }}>
                      "{record.city_county}" is not in the town list. Please select the correct town.
                    </div>
                  )}
                </Field>
                <Field label="Area" md={4}>
                  <CFormSelect
                    value={areaMode === 'other' ? '__other' : record.neighborhood_one || ''}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === '__other') {
                        setAreaMode('other')
                        setRecord((p) => ({ ...p, neighborhood_one: '', area_requested: true }))
                      } else {
                        setAreaMode('list')
                        setRecord((p) => ({ ...p, neighborhood_one: v, area_requested: false }))
                      }
                    }}
                    disabled={locked || !townId}
                  >
                    <option value="">{townId ? 'Select an area' : 'Select a town first'}</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.name}>{a.name}</option>
                    ))}
                    <option value="__other">Area not listed</option>
                  </CFormSelect>
                  {areaMode === 'other' && (
                    <>
                      <CFormInput
                        className="mt-2"
                        spellCheck
                        value={record.neighborhood_one}
                        placeholder="Type the area name"
                        onChange={(e) => set('neighborhood_one', e.target.value)}
                        disabled={locked}
                      />
                      <div style={{ color: '#6b7280', fontSize: '.8rem', marginTop: 4 }}>
                        KLS will review this area before adding it to the list.
                      </div>
                    </>
                  )}
                </Field>
              </Section>

              {/* ===== LOCATION (address) ===== */}
              {showAddress && (
                <Section
                  title="Location"
                  hint="This is private and not visible to other agents or clients."
                >
                  <Field label="Full address" required={!isEdit} md={12}>
                    <CFormTextarea
                      rows={2}
                      value={record.full_address}
                      onChange={(e) => set('full_address', e.target.value)}
                      disabled={ro}
                    />
                  </Field>
               </Section>
              )}

              {/* ===== NEARBY ===== */}
              <Section
                title="Attractions (optional)"
                hint="Well-known places near the property: malls, schools, cafés, landmarks. Clients will see this."
              >
                <Field label="" md={12}>
                  <CFormTextarea
                    rows={3}
                    spellCheck value={record.nearby}
                    placeholder="e.g. 5 min walk to Yaya Centre, near Kilimani Primary School, opposite Java House"
                    onChange={(e) => set('nearby', e.target.value)}
                    disabled={ro}
                  />
                </Field>
              </Section>

              {/* ===== PRICE ===== */}
              <Section title="Price">
                <Field label="Price" required md={6} note={priceText}>
                  <CInputGroup>
                    <CFormSelect
                      style={{ maxWidth: 90 }}
                      value={record.currency}
                      onChange={(e) => set('currency', e.target.value)}
                      disabled={locked}
                    >
                      {CurrencyOptions.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </CFormSelect>
                    <CFormInput
                      type="number"
                      min="0"
                      value={record.original_price}
                      onChange={(e) => set('original_price', e.target.value)}
                      disabled={locked}
                    />
                    {record.status === 'For Rent' && <CInputGroupText>/ month</CInputGroupText>}
                  </CInputGroup>
                </Field>
              </Section>

              {/* ===== DETAILS ===== */}
              <Section title="Details">
                {!type && (
                  <CCol md={12} className="text-medium-emphasis small mb-2">
                    Pick a property type above first.
                  </CCol>
                )}

                {isLand && (
                  <>
                    <Field label="Plot size" required md={4}>
                      <CFormInput
                        type="number"
                        min="0"
                        value={record.plot_size}
                        onChange={(e) => set('plot_size', e.target.value)}
                        disabled={ro}
                      />
                    </Field>
                    <Field label="Unit" required md={4}>
                      <CFormSelect
                        value={record.plot_unit}
                        onChange={(e) => set('plot_unit', e.target.value)}
                        disabled={ro}
                      >
                        {PlotUnitOptions.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </CFormSelect>
                    </Field>
                    <Field label="Title deed ready?" md={4}>
                      <Pills
                        options={['Yes', 'No']}
                        value={
                          record.title_deed_ready === '' ? '' : Number(record.title_deed_ready) === 1 ? 'Yes' : 'No'
                        }
                        onChange={(v) => set('title_deed_ready', v === 'Yes' ? 1 : 0)}
                        disabled={ro}
                      />
                    </Field>
                  </>
                )}

                {type && !isLand && (
                  <>
                    <Field label="Furnishing" md={12}>
                      <Pills
                        options={FurnishingOptions}
                        value={record.furnishing}
                        onChange={(v) => set('furnishing', record.furnishing === v ? '' : v)}
                        disabled={ro}
                      />
                    </Field>
                      
                     <Field label="Bedrooms" required={hasRooms} md={12}>
                      <Pills
                        options={BedroomPills}
                        value={bedPill(record.bed_rooms)}
                        onChange={(v) =>
                          set(
                            'bed_rooms',
                            v === 'Studio' ? '0' : v === '6+' ? (Number(record.bed_rooms) >= 6 ? record.bed_rooms : '6') : v,
                          )
                        }
                        disabled={ro}
                      />
                      {Number(record.bed_rooms) >= 6 && (
                        <CFormInput
                          type="number"
                          min="6"
                          className="mt-2"
                          style={{ maxWidth: 160 }}
                          placeholder="Number of bedrooms"
                          value={record.bed_rooms}
                          onChange={(e) => set('bed_rooms', e.target.value)}
                          disabled={ro}
                        />
                      )}
                    </Field>
                    <Field label="Bathrooms" required={hasRooms} md={3}>
                      <CFormInput
                        type="number"
                        min="0"
                        value={record.rest_rooms}
                        onChange={(e) => set('rest_rooms', e.target.value)}
                        disabled={ro}
                      />
                    </Field>
                    <Field label="Parking spaces" md={3}>
                      <CFormInput
                        type="number"
                        min="0"
                        value={record.parking_space}
                        onChange={(e) => set('parking_space', e.target.value)}
                        disabled={ro}
                      />
                    </Field>
                      <Field label="Size (m²)" required md={3}>
                      <CFormInput
                        type="number"
                        min="0"
                        value={record.sq_meter}
                        onChange={(e) => set('sq_meter', e.target.value)}
                        disabled={ro}
                      />
                    </Field>
                    <Field label="Year built" md={3}>
                      <CFormInput
                        type="number"
                        min="1900"
                        value={record.year_established}
                        onChange={(e) => set('year_established', e.target.value)}
                        disabled={ro}
                      />
                    </Field>
                    <Field label="Features" md={12}>
                      <TagPicker
                        defaults={DefaultFeatures}
                        value={record.features}
                        onChange={(v) => set('features', v)}
                        disabled={ro}
                        placeholder="Add a feature"
                      />
                    </Field>
                  </>
                )}
              </Section>

              {/* ===== BILLS (not for land) ===== */}
              {type && !isLand && (
                <Section title="Utilities & Expenses" hint="Tenants Are Responsible For">
                  <Field label="" md={12}>
                    <TagPicker
                      defaults={DefaultBills}
                      value={record.bills}
                      onChange={(v) => set('bills', v)}
                      disabled={ro}
                      placeholder="Add a bill"
                    />
                  </Field>
                </Section>
              )}

              {/* ===== DESCRIPTION ===== */}
              <Section title="Description">
                <Field label="Short description" required md={12} note="One line, max 255 characters.">
                  <CFormInput
                    maxLength={255}
                    spellCheck value={record.short_description}
                    onChange={(e) => set('short_description', e.target.value)}
                    disabled={ro}
                  />
                </Field>
                <Field label="Full description" md={12}>
                  <CFormTextarea
                    rows={5}
                    spellCheck value={record.long_description}
                    onChange={(e) => set('long_description', e.target.value)}
                    disabled={ro}
                  />
                </Field>
              </Section>

              {/* ===== DISCLOSURE ===== */}
              <Section
                title="Disclosure (optional)"
                hint="Disclose relevant property details. No contact information."
              >
                <Field label="" md={12}>
                  <CFormTextarea
                    rows={2}
                    spellCheck value={record.disclosure}
                    onChange={(e) => set('disclosure', e.target.value)}
                    disabled={ro}
                  />
                </Field>
              </Section>

              {/* ===== PHOTOS ===== */}
                            <Section title="Photos">
                <Field
                  label="Main photo"
                  required
                  md={12}
                  note={
                    isEdit && FeaturedImage.length
                      ? 'This new photo replaces the current main photo when you save.'
                      : 'Shown first on the listing.'
                  }
                >
                  <div className="d-flex flex-wrap gap-3">
                    {isEdit && existingMain && FeaturedImage.length === 0 && (
                      <ExistingTile src={APP_BASE_URL + existingMain} size={200} main />
                    )}
                    <PhotoPicker
                      files={FeaturedImage}
                      onChange={setFeaturedImage}
                      size={200}
                      mainLabel
                      disabled={photosLocked}
                      addLabel={isEdit && existingMain ? 'Change photo' : 'Add photo'}
                    />
                  </div>
                </Field>
                <Field
                  label="Gallery"
                  md={12}
                  note="Up to 20 photos per listing, including the main photo. You can select several at once. Click a photo to view it full size."
                >
                  <div className="d-flex flex-wrap gap-3">
                    {existingImages
                      .filter((img) => !removeIds.includes(img.id))
                      .map((img) => (
                        <ExistingTile
                          key={img.id}
                          src={APP_BASE_URL + img.property_image}
                          size={150}
                          onRemove={photosLocked ? null : () => setRemoveIds((p) => [...p, img.id])}
                        />
                      ))}
                    <PhotoPicker files={Image} onChange={setImage} multiple size={150} disabled={photosLocked} />
                  </div>
                </Field>
                {removeIds.length > 0 && (
                  <CCol md={12} className="small text-danger">
                    {removeIds.length} photo(s) will be removed when you save.{' '}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        setRemoveIds([])
                      }}
                    >
                      Undo
                    </a>
                  </CCol>
                )}
              </Section>

              {!ro && (
                <div className="d-flex justify-content-end gap-2">
                  <CButton color="secondary" variant="outline" onClick={() => props.onClose()}>
                    Cancel
                  </CButton>
                  <CButton
                    onClick={save}
                    disabled={isDisabled}
                    style={{ backgroundColor: GREEN, borderColor: GREEN, color: '#fff' }}
                  >
                    {isDisabled ? 'Saving...' : isEdit ? 'Save changes' : 'Submit for approval'}
                  </CButton>
                </div>
              )}
            </CForm>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  )
}